import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { FormikProps } from 'formik';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Button, Grid, Typography } from '@material-ui/core';

import { RegistrationForm as RegistrationFormType } from 'types';
import { ProofOfIdentityType } from 'enums';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { FormItemSelectField } from 'components/form-item/form-item-select-field';
import { FormItemCheckboxGroupField } from 'components/form-item/form-item-checkbox-group-field';
import { FormItemMultipleFileField } from 'components/form-item/form-item-multiple-file-field';
import { I18nCtx } from 'components/i18n/i18n-context';
import { getItemsByLocale } from 'components/i18n/locale/utils';
import { useFetchList } from 'components/common-hooks/fetch-list-hook';
import { useWhoIs } from 'components/auth/auth-hook';
import { ConfirmationBadatelnaRulesCheckbox } from 'modules/confirmation-checkbox/confirmation-badatelna-rules-checkbox';
import { ConfirmationPrivateInfoCheckbox } from 'modules/confirmation-checkbox/confirmation-private-info-checkbox';

import { fetchCountryCodes } from './registration-api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginTop: 50,
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
    },
    submitButton: {
      fontWeight: 'bold',
      color: 'white',
      textTransform: 'initial',
    },
    error: {
      color: theme.palette.error.main,
      fontWeight: 'bold',
      height: '2em',
    },
  })
);

export const RegistrationFormBasic = (
  props: FormikProps<RegistrationFormType>
) => {
  const classes = useStyles();
  const { locale } = useContext(I18nCtx);
  const { formatMessage } = useIntl();
  const { whoIsInfo } = useWhoIs();

  const { setFieldValue } = props;
  const whoIsInfoExist = !!whoIsInfo;

  const {
    sexOptions,
    proofOfIdentityTypeOptions,
    researchCategoryOptions,
    researchSheetTypeOptions,
  } = getItemsByLocale(locale);
  const filteredProofOfIdentityTypeOptions = proofOfIdentityTypeOptions.filter(
    (p) => whoIsInfoExist || p.id !== ProofOfIdentityType.CAS
  );

  const { items: countries } = useFetchList({
    fetchList: fetchCountryCodes,
    withRedirect: false,
  });

  useEffect(() => {
    if (whoIsInfoExist) {
      setFieldValue(
        'researchSheetDto.proofOfIdentityValue',
        whoIsInfo?.researchSheetDto?.proofOfIdentityValue
      );

      setFieldValue(
        'researchSheetDto.proofOfIdentityType',
        ProofOfIdentityType.CAS
      );
    }
  }, [setFieldValue, whoIsInfo, whoIsInfoExist]);

  return (
    <form onSubmit={props.handleSubmit}>
      <Grid container spacing={2} className={classes.center}>
        <Grid item xs={12} md={6}>
          <FormItemTextField
            name="firstName"
            label={formatMessage({
              id: 'registration.firstName',
              defaultMessage: 'Jméno *',
            })}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormItemTextField
            name="lastName"
            label={formatMessage({
              id: 'registration.lastName',
              defaultMessage: 'Příjmení *',
            })}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormItemTextField
            name="maidenName"
            label={formatMessage({
              id: 'registration.maidenName',
              defaultMessage: 'Rodné jméno',
            })}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormItemSelectField
            name="sex"
            label={formatMessage({
              id: 'registration.sex',
              defaultMessage: 'Pohlaví *',
            })}
            items={sexOptions}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="birthDate"
            type="date"
            label={formatMessage({
              id: 'registration.birthDate',
              defaultMessage: 'Datum narození *',
            })}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="birthPlace"
            label={formatMessage({
              id: 'registration.birthPlace',
              defaultMessage: 'Místo narození',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemSelectField
            name="citizenshipCountryId"
            label={formatMessage({
              id: 'registration.citizenshipCountryId',
              defaultMessage: 'Občanství *',
            })}
            disabled={whoIsInfoExist}
            items={countries}
          />
        </Grid>

        <Grid item xs={12}>
          <FormItemTextField
            name="permanentResidence.municipality"
            label={formatMessage({
              id: 'registration.permanentResidence.municipality',
              defaultMessage: 'Obec (adresa trvalého bydliště) *',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="permanentResidence.street"
            label={formatMessage({
              id: 'registration.permanentResidence.street',
              defaultMessage: 'Ulice (adresa trvalého bydliště) *',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="permanentResidence.zip"
            label={formatMessage({
              id: 'registration.permanentResidence.zip',
              defaultMessage: 'PSČ (adresa trvalého bydliště) *',
            })}
          />
        </Grid>

        <Grid item xs={12}>
          <FormItemTextField
            name="deliveryAddress.municipality"
            label={formatMessage({
              id: 'registration.deliveryAddress.municipality',
              defaultMessage: 'Obec (adresa  pro doručení)',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="deliveryAddress.street"
            label={formatMessage({
              id: 'registration.deliveryAddress.street',
              defaultMessage: 'Ulice (adresa  pro doručení)',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="deliveryAddress.zip"
            label={formatMessage({
              id: 'registration.deliveryAddress.zip',
              defaultMessage: 'PSČ (adresa  pro doručení)',
            })}
          />
        </Grid>

        <Grid item xs={12}>
          <FormItemTextField
            name="phone"
            label={formatMessage({
              id: 'registration.phone',
              defaultMessage: 'Telefon',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="email"
            type="email"
            label={formatMessage({
              id: 'registration.email',
              defaultMessage: 'Emailová adresa *',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="dataBox"
            label={formatMessage({
              id: 'registration.dataBoxAddress',
              defaultMessage: 'Adresa datové schránky',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" align="left" className={classes.section}>
            {formatMessage({
              id: 'registration.researchSheetDto.info',
              defaultMessage:
                'Další údaje potřebné pro vytvoření Badatelského listu.',
            })}
          </Typography>
          <FormItemSelectField
            name="researchSheetDto.proofOfIdentityType"
            label={formatMessage({
              id: 'registration.researchSheetDto.proofOfIdentityType',
              defaultMessage: 'Osobní doklad *',
            })}
            items={filteredProofOfIdentityTypeOptions}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="researchSheetDto.proofOfIdentityValue"
            label={formatMessage({
              id: 'registration.researchSheetDto.proofOfIdentityValue',
              defaultMessage: 'Číslo osobního dokladu *',
            })}
            disabled={whoIsInfoExist}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="researchSheetDto.researchTopic"
            label={formatMessage({
              id: 'registration.researchSheetDto.researchTopic',
              defaultMessage: 'Téma studia *',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemSelectField
            name="researchSheetDto.type"
            label={formatMessage({
              id: 'registration.researchSheetDto.type',
              defaultMessage: 'Účel nahlížení *',
            })}
            items={researchSheetTypeOptions}
          />
        </Grid>
        {props.values?.researchSheetDto?.type === 'CREDENTIAL' && (
          <>
            <Grid item xs={12}>
              <FormItemMultipleFileField
                name="researchSheetDto.inspectionRequest"
                label={formatMessage({
                  id: 'registration.researchSheetDto.inspectionRequest',
                  defaultMessage: 'Přílohy žádosti',
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormItemTextField
                name="researchSheetDto.legalEntityName"
                label={formatMessage({
                  id: 'registration.researchSheetDto.legalEntityName',
                  defaultMessage: 'Název právnické osoby *',
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormItemTextField
                name="researchSheetDto.legalEntityOffice.municipality"
                label={formatMessage({
                  id:
                    'registration.researchSheetDto.legalEntityOffice.municipality',
                  defaultMessage: 'Obec (adresa právnické osoby) *',
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormItemTextField
                name="researchSheetDto.legalEntityOffice.street"
                label={formatMessage({
                  id: 'registration.researchSheetDto.legalEntityOffice.street',
                  defaultMessage: 'Ulice (adresa právnické osoby) *',
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormItemTextField
                name="researchSheetDto.legalEntityOffice.zip"
                label={formatMessage({
                  id: 'registration.researchSheetDto.legalEntityOffice.zip',
                  defaultMessage: 'PSČ (adresa právnické osoby) *',
                })}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Typography variant="body1" align="left" className={classes.section}>
            {formatMessage({
              id: 'registration.researchSheetDto.researchCategories',
              defaultMessage: 'Zaměření nahlížení *',
            })}
          </Typography>
          <FormItemCheckboxGroupField
            name="researchSheetDto.researchCategories"
            items={researchCategoryOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <FormItemTextField
            name="researchSheetDto.orderDescription"
            label={formatMessage({
              id: 'registration.orderDescription',
              defaultMessage: 'Popis objednávky',
            })}
            multiline={true}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" align="left" className={classes.section}>
            {formatMessage(
              {
                id: 'registration.declareInfo',
                defaultMessage:
                  'Prohlašuji, že jsem si v souladu s příslušnou právní úpravou plně vědom své osobní odpovědnosti za nakládání s informacemi, jež jsem získal nahlížením do archiválií.<br></br>Prohlašuji, že jsem se seznámil s ustanoveními badatelského řádu a beru na vědomí, že při porušení základních povinností mi může být další nahlížení do archiválií odepřeno, případně udělený souhlas odvolán.',
              },
              { br: () => <br /> }
            )}
          </Typography>
          <ConfirmationBadatelnaRulesCheckbox name="confirmation" />
          <ConfirmationPrivateInfoCheckbox name="confirmation2" />
        </Grid>
        <Grid item xs={12}>
          {!isEmpty(props.errors) && (
            <div className={classes.error}>Formulář obsahuje chyby</div>
          )}
          <Button variant="contained" color="primary" type="submit">
            <Typography
              component="span"
              variant="body2"
              className={classes.submitButton}
            >
              {formatMessage({
                id: 'registration.submitButton',
                defaultMessage: 'Odeslat žádost o registraci',
              })}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
